import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "@tanstack/react-router";

import { createProjectWithoutTemplate } from "@synthesis/shared-services/project-gge/projectController/client";
import { useAuthState } from "@synthesis/shared-auth";
import { IconCheckmark } from "@synthesis/shared-icons";
import { useGetUserGroups } from "@synthesis/shared-services/auth/group/hooks";
import type { GroupDto } from "@synthesis/shared-services/auth/group/types";
import { Button, setSnack } from "@synthesis/shared-ui";
import { logger } from "@synthesis/shared-utils";

import { useUserRolesGGE } from "../../../hooks/use-user-roles-gge";
import { AccessDenied } from "../../../components/access-denied";
import { ListOfExpertsPage } from "../../list-of-experts-page";
import type { RoleAssignmentType } from "../../list-of-experts-page/list-of-experts-page.lib";
import { type CreateProjectFormData, createCreateProjectSchema } from "./create-project-schema";
import {
  FORM_SESSION_KEY,
  assignExpertsAndOwnerGroup,
  buildCreateProjectBody,
  expertsSelectionIsValid,
  getCreateProjectErrorMessage,
  toStepsProps,
} from "./create-project-stage-1-page.lib";
import { RenderFormItem, StepStatusBar } from "./local-components";

export const CreateProjectStage1Page = () => {
  const { currentUser } = useAuthState();
  const navigate = useNavigate();
  const roles = useUserRolesGGE();
  const methods = useForm<CreateProjectFormData>({
    resolver: zodResolver(createCreateProjectSchema(roles.МОДЕРИРОВАНИЕ)),
    defaultValues: { name: "", code: "", description: "", objectName: "" },
    mode: "onChange",
  });

  const [step, setStep] = React.useState<1 | 2>(1);
  const userId = currentUser?.id ?? "";
  const { data: userGroups = null } = useGetUserGroups({ path: { userId } }, { query: { enabled: !!userId } });
  const groupsForProject = React.useRef<GroupDto[] | null>(null);
  const usersForProject = React.useRef<RoleAssignmentType[] | null>(null);

  const onSubmit = async (data: CreateProjectFormData) => {
    if (step === 1) {
      sessionStorage.setItem(FORM_SESSION_KEY, JSON.stringify(data));
      setStep(2);
      return;
    }

    if (!expertsSelectionIsValid(usersForProject.current)) {
      setSnack({ title: "Выбраны не все эксперты", messageType: "error" });
      return;
    }

    if (!currentUser) return;

    try {
      const { data: project } = await createProjectWithoutTemplate({ body: buildCreateProjectBody(data) });
      const projectId = project.id ?? "";

      await assignExpertsAndOwnerGroup(projectId, usersForProject.current, groupsForProject.current, userGroups?.[0]?.id ?? undefined);

      sessionStorage.removeItem(FORM_SESSION_KEY);
      navigate({ to: "/project/$projectId/dashboard", params: { projectId } });
      setSnack({
        title: "Проект экспертизы успешно создан",
        icon: <IconCheckmark className="size-4" />,
        messageType: "success",
      });
    } catch (error) {
      const message = getCreateProjectErrorMessage(error);

      if (message.includes("Project with name") && message.includes("externalId")) {
        setSnack({
          title: "Невозможно создать проект",
          description: `Проект с наименованием ${data.name} и номером ${data.code} уже существует`,
          messageType: "error",
        });
        return;
      }
      if (message.includes("externalId")) {
        setSnack({
          title: "Невозможно создать проект",
          description: `Проект с номером ${data.code} уже существует`,
          messageType: "error",
        });
      }
      logger.error(error);
    }
  };

  React.useEffect(() => {
    const sessionDataJson = sessionStorage.getItem(FORM_SESSION_KEY);
    if (sessionDataJson) {
      methods.reset(JSON.parse(sessionDataJson) as CreateProjectFormData);
    }
  }, [methods]);

  const handleBack = () => {
    if (step === 1) {
      sessionStorage.removeItem(FORM_SESSION_KEY);
      navigate({ to: "/" });
      return;
    }
    setStep(1);
  };

  const steps = toStepsProps(step);

  if (roles.НЕТ || roles.ПРОСМОТР) {
    return <AccessDenied />;
  }

  return (
    <FormProvider {...methods}>
      <form className="mx-auto w-full px-8 py-6" onSubmit={methods.handleSubmit(onSubmit)}>
        <header className="flex flex-col justify-between">
          <StepStatusBar steps={steps} />
          <h1 className="heading-h4 pb-5">{step === 1 ? steps.step1.title : steps.step2.title}</h1>
        </header>
        <main className="flex flex-col rounded-2xl bg-white">
          {step === 1 && (
            <div className="relative flex w-full flex-1 flex-col">
              <div className="flex w-full flex-col gap-6 px-6 py-8">
                <RenderFormItem
                  title="Наименование"
                  subtitle="Наименование проекта экспертизы"
                  name="name"
                  placeholder="Введите наименование проекта"
                />
                <RenderFormItem title="Номер" subtitle="Номер проекта экспертизы" mask name="code" placeholder="Введите номер проекта" />
                <RenderFormItem
                  title="Организация"
                  subtitle="Наименование организации"
                  name="objectName"
                  placeholder="Введите наименование организации"
                  userGroupName={userGroups?.[0]?.name ?? undefined}
                />
                <RenderFormItem
                  title="Документация"
                  subtitle="Ссылка на документацию"
                  name="description"
                  placeholder="https://3d.platformaexpert.ru/projects"
                />
              </div>
            </div>
          )}
          {step === 2 && <ListOfExpertsPage groupsForProject={groupsForProject} usersForProject={usersForProject} />}
        </main>
        <div className="mt-4 flex h-11 justify-between text-sm font-bold">
          <Button onClick={handleBack} size="lg">
            {step === 1 ? "Отмена" : "Назад"}
          </Button>
          <Button type="submit" size="lg" theme="primary">
            Продолжить
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
